import { isNullish } from '@adornis/assert/isNullish.js';
import { alertDialog } from '@adornis/dialog/dialog-functions.js';
import { parseAmountToCents } from '@adornis/formfield/fields/currency-input-parsing.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { numberToDisplay } from '@adornis/formfield/fields/number-input-parsing.js';
import type { FunctionalFormFieldProps } from '@adornis/formfield/functional-form-field.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { BillingPositionPriceSymbols } from '../entities/BillingPosition.js';

export const formatBillingPositionPrice = (priceInCents: number | null | undefined): string => {
  if (isNullish(priceInCents)) return 'Selbstauswahl';

  const priceLabels: Record<number, string> = {
    '-100': 'Dynamischer Preis',
    '-200': 'Pausiert',
    '-300': 'Befreit',
    '-400': 'Vollständige Förderung',
  };

  return (
    priceLabels[priceInCents] ??
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(priceInCents / 100)
  );
};

/** Cents in the document, a typed euro amount in the field — `25,50` and `25.50` both read as 2550. */
function CentsAsEuroTextFieldMiddleware(field: FunctionalFormFieldProps<number>): FunctionalFormFieldProps<string> {
  return {
    ...field,
    // Plain euro text, so the symbol selectables (`-1`, `-2`, …) stay identical to their own value.
    value: isNullish(field.value) ? BillingPositionPriceSymbols.SELF_SELECTION : numberToDisplay(field.value / 100),
    onChanged: value => field.onChanged?.(isNullish(value) ? null : parseAmountToCents(value)),
    onPicked: value => {
      if (value === BillingPositionPriceSymbols.SELF_SELECTION || isNullish(value)) {
        field.onPicked?.(null);
        return;
      }

      const cents = parseAmountToCents(value);
      if (isNullish(cents)) {
        void alertDialog(`„${value}“ ist kein gültiger Betrag. Bitte z.B. 25 oder 25,50 eingeben.`);
        return;
      }

      if (cents === 0) {
        void alertDialog(
          'Die 0 kann verschiedene Sachen beudeten. Bitte einen expliziten Eintrag aus der Liste nutzen.',
        );
      }

      field.onPicked?.(cents);
    },
  };
}

export const BillingPositionPriceInput = stateful((props: FunctionalFormFieldProps<number>) => {
  return DropdownSelectionInput({
    ...CentsAsEuroTextFieldMiddleware(props),
    placeholder: 'z.B. 25€ (Leer für Selbstauswahl)',
    placeholderMode: 'hide',
    freeText: true,
    selectables: Object.values(BillingPositionPriceSymbols),
    suffix: '€',
    renderString: value => {
      if (value === BillingPositionPriceSymbols.SELF_SELECTION || isNullish(value) || value === '')
        return formatBillingPositionPrice(null);
      const cents = parseAmountToCents(value);
      // Unparseable text is shown as typed rather than as a price it is not.
      return isNullish(cents) ? value : formatBillingPositionPrice(cents);
    },
  });
});
